/**
 * O que o estudo faz na tela e o PDF não faz: destaque ao passar o mouse em
 * cartões e linhas, a dica com os valores dos gráficos e o botão que troca a
 * estratégia de "Depois da contemplação".
 *
 * As seções chegam como HTML (estudoPaginas.ts, o mesmo do PDF), então tudo é
 * ligado por delegação no elemento que as contém. Os gráficos trazem os
 * valores em `data-dica` (linhas) e `data-barra` (barras), de graficosSvg.ts.
 */

export const ESTILO_INTERATIVO = `
.estudo-vivo .estudo-cartao{transition:transform .18s ease,box-shadow .18s ease}
.estudo-vivo .estudo-cartao:hover{transform:translateY(-3px);box-shadow:0 10px 26px rgba(31,44,64,.16),0 0 0 2px #C4AD8E}
.estudo-vivo tbody tr{transition:background-color .12s}
.estudo-vivo tbody tr:hover{background-color:#E9EEF5 !important}
.estudo-vivo .estudo-linha{border-radius:6px;transition:background-color .12s}
.estudo-vivo .estudo-linha:hover{background-color:#F3EDE3;box-shadow:-8px 0 0 #F3EDE3,8px 0 0 #F3EDE3}
.estudo-vivo svg [data-barra]{transition:opacity .15s}
.estudo-vivo svg:hover [data-barra]{opacity:.45}
.estudo-vivo svg [data-barra]:hover{opacity:1}
.estudo-vivo .estudo-estrategia-botao{font:inherit;text-align:left;cursor:pointer;border-radius:12px;padding:8px 14px;border:1.5px solid #D9DFE7;background:#fff;color:#1A2433;transition:border-color .15s,background-color .15s}
.estudo-vivo .estudo-estrategia-botao:hover{border-color:#C4AD8E}
.estudo-vivo .estudo-estrategia-botao[aria-selected="true"]{background:#1F2C40;border-color:#1F2C40;color:#fff}
.estudo-vivo .estudo-estrategia-botao:focus-visible{outline:2px solid #C4AD8E;outline-offset:2px}
.estudo-vivo [data-estrategia]{transition:opacity .25s ease}
.estudo-vivo .estudo-oculta{visibility:hidden;opacity:0;pointer-events:none}
.estudo-dica{position:fixed;z-index:60;pointer-events:none;background:#1F2C40;color:#fff;border-radius:10px;padding:9px 12px;font:12px/1.5 Montserrat,"Helvetica Neue",Arial,sans-serif;box-shadow:0 10px 30px rgba(0,0,0,.3);min-width:150px;font-variant-numeric:tabular-nums;opacity:0;transition:opacity .12s}
.estudo-dica.on{opacity:1}
.estudo-dica b{display:block;font-size:10.5px;letter-spacing:.1em;text-transform:uppercase;color:#C4AD8E;margin-bottom:3px}
.estudo-dica .l{display:flex;align-items:center;gap:10px;justify-content:space-between}
.estudo-dica .l span{display:flex;align-items:center;gap:6px;color:rgba(255,255,255,.75)}
.estudo-dica .l i{width:9px;height:9px;border-radius:50%;display:inline-block}
@media (prefers-reduced-motion:reduce){.estudo-vivo *{transition:none !important}}
`;

type DicaLinhas = {
  tipo: 'linhas';
  x0: number;
  w: number;
  topo: number;
  h: number;
  rotulos: string[];
  series: Array<{ nome: string; cor: string; y: number[]; v: string[] }>;
};

const NS = 'http://www.w3.org/2000/svg';

/** Texto vindo do HTML do estudo: escapado de novo antes de virar innerHTML da dica. */
const esc = (s: unknown) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
const cor = (s: string) => (/^#[0-9a-f]{3,8}$/i.test(s) ? s : '#C4AD8E');

export const lerDica = (svg: Element): DicaLinhas | null => {
  try {
    const d = JSON.parse(svg.getAttribute('data-dica') ?? '');
    return d?.tipo === 'linhas' && Array.isArray(d.rotulos) && Array.isArray(d.series) ? d : null;
  } catch {
    return null;
  }
};

/**
 * Índice do ponto sob o mouse. O SVG pode estar com faixas vazias dos lados
 * (altura limitada nos slides, preserveAspectRatio padrão), então a conta
 * desconta a sobra antes de converter para a coordenada do viewBox.
 */
export const indiceNoPonto = (
  d: Pick<DicaLinhas, 'x0' | 'w' | 'rotulos'>,
  caixa: { left: number; width: number; height: number },
  viewBox: { width: number; height: number },
  clientX: number,
) => {
  const escala = Math.min(caixa.width / viewBox.width, caixa.height / viewBox.height) || 1;
  const sobra = (caixa.width - viewBox.width * escala) / 2;
  const vx = (clientX - caixa.left - sobra) / escala;
  const n = d.rotulos.length;
  if (n < 2) return 0;
  return Math.max(0, Math.min(n - 1, Math.round(((vx - d.x0) / d.w) * (n - 1))));
};

/** Liga destaques, dicas e troca de estratégia nas seções dentro de `raiz`. Devolve o desligar. */
export const ligarInteracoes = (raiz: HTMLElement): (() => void) => {
  raiz.classList.add('estudo-vivo');
  const dica = document.createElement('div');
  dica.className = 'estudo-dica';
  dica.setAttribute('role', 'tooltip');
  document.body.appendChild(dica);

  const tirarGuia = () => raiz.querySelectorAll('.estudo-guia').forEach((g) => g.remove());
  const esconder = () => {
    dica.classList.remove('on');
    tirarGuia();
  };
  const mostrar = (html: string, x: number, y: number) => {
    dica.innerHTML = html;
    dica.classList.add('on');
    const r = dica.getBoundingClientRect();
    let l = x + 16;
    let t = y - r.height - 14;
    if (l + r.width > window.innerWidth - 8) l = x - r.width - 16;
    if (t < 8) t = y + 18;
    dica.style.left = `${Math.max(8, l)}px`;
    dica.style.top = `${t}px`;
  };

  const mover = (e: MouseEvent) => {
    const alvo = e.target as Element | null;
    const barra = alvo?.closest?.('[data-barra]');
    if (barra) {
      tirarGuia();
      const [titulo, ...resto] = (barra.getAttribute('data-barra') ?? '').split(' · ');
      mostrar(`<b>${esc(titulo)}</b>${esc(resto.join(' · '))}`, e.clientX, e.clientY);
      return;
    }
    const svg = alvo?.closest?.('svg[data-dica]') as SVGSVGElement | null;
    const d = svg && lerDica(svg);
    if (!svg || !d) {
      esconder();
      return;
    }
    const vb = svg.viewBox.baseVal;
    const i = indiceNoPonto(d, svg.getBoundingClientRect(), vb, e.clientX);
    const xi = d.x0 + (i / Math.max(1, d.rotulos.length - 1)) * d.w;
    let g = svg.querySelector('.estudo-guia');
    if (!g) {
      g = document.createElementNS(NS, 'g');
      g.setAttribute('class', 'estudo-guia');
      g.setAttribute('pointer-events', 'none');
      svg.appendChild(g);
    }
    const series = d.series.filter((s) => Number.isFinite(s.y?.[i]));
    g.innerHTML =
      `<line x1="${xi}" x2="${xi}" y1="${d.topo}" y2="${d.topo + d.h}" stroke="#1F2C40" stroke-opacity=".35" stroke-width="1"/>` +
      series.map((s) => `<circle cx="${xi}" cy="${s.y[i]}" r="5" fill="#fff" stroke="${cor(s.cor)}" stroke-width="3"/>`).join('');
    const linhas = [...series]
      .sort((a, b) => a.y[i] - b.y[i])
      .map((s) => `<div class="l"><span><i style="background:${cor(s.cor)}"></i>${esc(s.nome)}</span><strong>${esc(s.v[i])}</strong></div>`)
      .join('');
    mostrar(`<b>${esc(d.rotulos[i])}</b>${linhas}`, e.clientX, e.clientY);
  };

  const clicar = (e: MouseEvent) => {
    const botao = (e.target as Element | null)?.closest?.('[data-estrategia-botao]');
    if (!botao) return;
    const caixa = botao.closest('.estudo-estrategias');
    const n = botao.getAttribute('data-estrategia-botao');
    caixa?.querySelectorAll('[data-estrategia-botao]').forEach((b) => b.setAttribute('aria-selected', String(b === botao)));
    caixa?.querySelectorAll('[data-estrategia]').forEach((p) => p.classList.toggle('estudo-oculta', p.getAttribute('data-estrategia') !== n));
  };

  raiz.addEventListener('mousemove', mover);
  raiz.addEventListener('mouseleave', esconder);
  raiz.addEventListener('click', clicar);
  return () => {
    raiz.removeEventListener('mousemove', mover);
    raiz.removeEventListener('mouseleave', esconder);
    raiz.removeEventListener('click', clicar);
    raiz.classList.remove('estudo-vivo');
    dica.remove();
  };
};
